import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import { SendPostRequest } from "../http";
import { toast } from "react-toastify";
import "./DemoBooking.css";

const VALID_PRODUCTS = ["Web-Builder", "LARIS", "MAPS", "Other"];

function DemoBooking() {
  document.title = "Request a Demo :: D-Right Technologies";

  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [orgType, setOrgType] = useState("");
  const [product, setProduct] = useState("");
  const [isOrgOpen, setIsOrgOpen] = useState(false);
  const [isProductOpen, setIsProductOpen] = useState(false);
  const orgDropdownRef = useRef(null);
  const productDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(event.target)) {
        setIsOrgOpen(false);
      }
      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target)) {
        setIsProductOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Pre-select product from URL query param (e.g. ?product=LARIS)
  useEffect(() => {
    const productParam = searchParams.get("product");
    if (productParam && VALID_PRODUCTS.includes(productParam)) {
      setProduct(productParam);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    setLoading(true);
    const formData = new FormData(form);
    const data = Object.fromEntries(formData.entries());

    try {
      // Try to submit to API, but show success regardless
      await SendPostRequest("book-demo", {
        form_data: data,
        recipient_email: "info@dright.net",
      });
    } catch (error) {
      console.error(error);
      // Continue to show success even if API fails
    }

    // Show success message regardless of API response
    form.reset();
    // Reset form state
    setOrgType("");
    setProduct("");
    setIsOrgOpen(false);
    setIsProductOpen(false);
    toast.success("Thank you! We've received your demo request and will contact you soon.");
    
    setLoading(false);
  };

  return (
    <div>
      <Header />
      <div className="main demo-booking-page no-banner-top-padding">
        <section
          className="page-header-section ptb-100 bg-image"
          image-overlay="8"
        >
          <div
            className="background-image-wraper"
            style={{
              background: `url('/assets/img/contactus-banner.jpg')`,
              // background: `url('/assets/img/slider-bg-1.jpg')`,
              opacity: "1",
            }}
          ></div>
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-9 col-lg-7">
                <div className="page-header-content text-white pt-4">
                  <h1 className="text-white mb-0">Request a Demo</h1>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="contact-us-section ptb-100 demo-booking-section">
          <div className="container">
            <div className="row demo-booking-form-wrapper">
              <div className="demo-booking-form-container">
                <div className="contact-us-form gray-light-bg rounded p-5 demo-booking-card">
                  <form id="requestDemoForm" className="contact-us-form" onSubmit={handleSubmit}>
                    <div className="form-group">
                      <input
                        type="text"
                        className="form-control"
                        name="full_name"
                        placeholder="Full Name"
                        required
                        onInvalid={(e) => {
                          e.target.setCustomValidity("Full name is required");
                        }}
                        onInput={(e) => {
                          e.target.setCustomValidity("");
                        }}
                      />
                    </div>
                    <div className="form-group">
                      <input
                        type="email"
                        className="form-control"
                        name="email"
                        placeholder="Email Address"
                        required
                        onInvalid={(e) => {
                          if (e.target.validity.valueMissing) {
                            e.target.setCustomValidity("Email address is required");
                          } else if (e.target.validity.typeMismatch) {
                            e.target.setCustomValidity("Please enter a valid email address");
                          }
                        }}
                        onInput={(e) => {
                          e.target.setCustomValidity("");
                        }}
                      />
                    </div>
                    <div className="form-group">
                      <input
                        type="tel"
                        className="form-control"
                        name="phone_number"
                        placeholder="Phone Number"
                        required
                        onInvalid={(e) => {
                          e.target.setCustomValidity("Phone number is required");
                        }}
                        onInput={(e) => {
                          e.target.setCustomValidity("");
                        }}
                      />
                    </div>
                    <div className="form-group">
                      {/* Hidden native select for form validity + FormData */}
                      <select
                        className="form-control demo-booking-native-select"
                        name="organization_type"
                        required
                        value={orgType}
                        onChange={(e) => setOrgType(e.target.value)}
                        onInvalid={(e) => {
                          e.target.setCustomValidity("Organization type is required");
                        }}
                        onInput={(e) => {
                          e.target.setCustomValidity("");
                        }}
                      >
                        <option value="" disabled>
                          Organization Type
                        </option>
                        <option value="Doctor">Doctor</option>
                        <option value="Clinic">Clinic</option>
                        <option value="Hospital">Hospital</option>
                        <option value="Laboratory">Laboratory</option>
                        <option value="Pharmacy">Pharmacy</option>
                      </select>

                      {/* Web-Builder style dropdown UI */}
                      <div className="demo-booking-dropdown" ref={orgDropdownRef}>
                        <button
                          type="button"
                          className="demo-booking-dropdown-toggle"
                          onClick={() => {
                            setIsOrgOpen((v) => !v);
                            setIsProductOpen(false);
                          }}
                        >
                          <span className={!orgType ? "placeholder" : ""}>{orgType || "Organization Type"}</span>
                          <i className={`fas fa-chevron-${isOrgOpen ? "up" : "down"}`}></i>
                        </button>
                        {isOrgOpen && (
                          <div className="demo-booking-dropdown-menu">
                            {["Doctor", "Clinic", "Hospital", "Laboratory", "Pharmacy"].map((opt) => (
                              <button
                                key={opt}
                                type="button"
                                className={`demo-booking-dropdown-item ${orgType === opt ? "active" : ""}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setOrgType(opt);
                                  setIsOrgOpen(false);
                                }}
                              >
                                {opt}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="form-group">
                      <input
                        type="text"
                        className="form-control"
                        name="organization_name"
                        placeholder="Organization Name"
                        required
                        onInvalid={(e) => {
                          e.target.setCustomValidity("Organization name is required");
                        }}
                        onInput={(e) => {
                          e.target.setCustomValidity("");
                        }}
                      />
                    </div>
                    <div className="form-group">
                      {/* Hidden native select for form validity + FormData */}
                      <select
                        className="form-control demo-booking-native-select"
                        name="product"
                        required
                        value={product}
                        onChange={(e) => setProduct(e.target.value)}
                        onInvalid={(e) => {
                          e.target.setCustomValidity("Product is required");
                        }}
                        onInput={(e) => {
                          e.target.setCustomValidity("");
                        }}
                      >
                        <option value="" disabled>
                          Product
                        </option>
                        <option value="Web-Builder">Web-Builder</option>
                        <option value="LARIS">LARIS</option>
                        <option value="MAPS">MAPS</option>
                        <option value="Other">Other</option>
                      </select>

                      {/* Web-Builder style dropdown UI */}
                      <div className="demo-booking-dropdown" ref={productDropdownRef}>
                        <button
                          type="button"
                          className="demo-booking-dropdown-toggle"
                          onClick={() => {
                            setIsProductOpen((v) => !v);
                            setIsOrgOpen(false);
                          }}
                        >
                          <span className={!product ? "placeholder" : ""}>{product || "Product"}</span>
                          <i className={`fas fa-chevron-${isProductOpen ? "up" : "down"}`}></i>
                        </button>
                        {isProductOpen && (
                          <div className="demo-booking-dropdown-menu">
                            {["Web-Builder", "LARIS", "MAPS", "Other"].map((opt) => (
                              <button
                                key={opt}
                                type="button"
                                className={`demo-booking-dropdown-item ${product === opt ? "active" : ""}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setProduct(opt);
                                  setIsProductOpen(false);
                                }}
                              >
                                {opt}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="form-group mb-4 text-center">
                      <button 
                        type="submit" 
                        className="btn btn-brand-02"
                        disabled={loading}
                      >
                        {loading ? "Submitting..." : "Send Request"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}

export default DemoBooking;
